import { useState, useEffect, useCallback, useRef } from 'react';
import useAuthStore from '../store/authStore';
import api from '../api/offlineClient';

const ROLES_ADMIN = ['admin', 'jefe', 'administrativo'];

// Endesa identifica cada instalación por su dirección; los demás clientes traen
// la calle real en "dirección" pero el nombre que identifica la instalación (el
// cliente final) viene en la columna "usuario" del Excel (usuario_responsable).
// Solo cambia lo que se muestra — las llamadas a la API siguen usando dirección.
function instNombre(inst) {
  const esEndesa = (inst?.cliente || '').toUpperCase().includes('ENDESA');
  if (esEndesa) return inst?.direccion || '';
  return inst?.usuario_responsable || inst?.direccion || '';
}

const ESTADO_OPTS = [
  { val: 'ok',            label: 'OK · Operativa' },
  { val: 'revision',      label: 'En revisión' },
  { val: 'averia',        label: 'Avería' },
  { val: 'mantenimiento', label: 'En mantenimiento' },
  { val: 'baja',          label: 'Baja / Inactiva' },
];

const ESTADO_COLOR = {
  ok: '#4ade80', revision: '#fbbf24', averia: '#f87171',
  mantenimiento: '#60a5fa', baja: '#6b7280',
};

export default function Actas() {
  const user    = useAuthStore(s => s.user);
  const isAdmin = ROLES_ADMIN.includes(user?.rol);

  // List state
  const [search, setSearch]           = useState('');
  const [applied, setApplied]         = useState('');
  const [page, setPage]               = useState(1);
  const [instList, setInstList]       = useState([]);
  const [pagination, setPagination]   = useState({ total: 0, pages: 1 });
  const [loadingList, setLoadingList] = useState(false);
  const debounceRef                   = useRef(null);

  // Detail state
  const [selectedInst, setSelectedInst] = useState(null);
  const [actas, setActas]               = useState([]);
  const [loadingActas, setLoadingActas] = useState(false);
  const [showContactoDetail, setShowContactoDetail] = useState(false);

  // Modal state
  const [modal, setModal]                 = useState(false);
  const [tipo, setTipo]                   = useState('actualizacion');
  const [titulo, setTitulo]               = useState('');
  const [trabajo, setTrabajo]             = useState('');
  const [materiales, setMateriales]       = useState('');
  const [observaciones, setObservaciones] = useState('');
  const [estadoInst, setEstadoInst]       = useState('ok');
  const [fecha, setFecha]                 = useState('');
  const [saving, setSaving]               = useState(false);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    try {
      const params = new URLSearchParams({ search: applied, page, limit: 20 });
      const r = await api.get(`/api/actas/instalaciones?${params}`);
      setInstList(r.data || []);
      setPagination({ total: r.total || 0, pages: r.pages || 1 });
    } catch { setInstList([]); }
    finally { setLoadingList(false); }
  }, [applied, page]);

  useEffect(() => { if (!selectedInst) loadList(); }, [loadList, selectedInst]);

  const loadActas = useCallback(async () => {
    if (!selectedInst) return;
    setLoadingActas(true);
    try {
      const r = await api.get(`/api/actas/list?direccion=${encodeURIComponent(selectedInst.direccion)}&limit=100`);
      setActas(r.data || []);
    } catch { setActas([]); }
    finally { setLoadingActas(false); }
  }, [selectedInst]);

  useEffect(() => { loadActas(); }, [loadActas]);
  useEffect(() => { setShowContactoDetail(false); }, [selectedInst?.direccion]);

  function handleSearch(e) {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => { setApplied(val); setPage(1); }, 350);
  }

  function openModal() {
    setTipo('actualizacion'); setTitulo(''); setTrabajo('');
    setMateriales(''); setObservaciones(''); setEstadoInst('ok');
    setFecha(new Date().toISOString().split('T')[0]);
    setModal(true);
  }

  async function submitActa() {
    setSaving(true);
    try {
      const r = await api.post('/api/actas/create', {
        installation_id: selectedInst.installation_ids[0],
        tipo,
        titulo:        titulo        || null,
        trabajo:       trabajo       || null,
        materiales:    materiales    || null,
        observaciones: observaciones || null,
        estado_inst:   estadoInst,
        fecha,
      });
      setActas(prev => [{ ...r.data, user_nombre: user.nombre }, ...prev]);
      setInstList(prev => prev.map(i =>
        i.id === selectedInst.id
          ? { ...i, acta_id: r.data.id, acta_tipo: tipo, acta_titulo: titulo || null,
              acta_trabajo: trabajo || null, acta_estado: estadoInst,
              acta_fecha: fecha, acta_user: user.nombre }
          : i
      ));
      setModal(false);
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  }

  async function deleteActa(id) {
    if (!window.confirm('¿Eliminar esta entrada?')) return;
    try {
      await api.delete(`/api/actas/${id}`);
      setActas(prev => prev.filter(a => a.id !== id));
    } catch (e) { alert(e.message); }
  }

  // ── Vista detalle ──────────────────────────────────────
  if (selectedInst) {
    return (
      <div style={{ padding: 'clamp(12px,3vw,24px)', maxWidth: 860, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, flexWrap: 'wrap' }}>
          <button onClick={() => setSelectedInst(null)}
            style={{ background: 'var(--surface)', border: '1px solid var(--border)',
              color: 'var(--text-dim)', borderRadius: 7, padding: '6px 14px',
              cursor: 'pointer', fontSize: 13, flexShrink: 0 }}>
            ← Volver
          </button>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div
              onClick={() => setShowContactoDetail(s => !s)}
              title="Ver teléfono de contacto"
              style={{ fontSize: 16, fontWeight: 800, color: 'var(--text)', cursor: 'pointer',
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              📍 {instNombre(selectedInst)}
            </div>
          </div>
          <button onClick={openModal}
            style={{ background: 'var(--amber)', color: '#000', border: 'none',
              padding: '8px 16px', borderRadius: 7, cursor: 'pointer',
              fontWeight: 800, fontSize: 12, flexShrink: 0 }}>
            + Nueva entrada
          </button>
        </div>

        {showContactoDetail && <ContactoBox direccion={selectedInst.direccion} />}

        {loadingActas ? (
          <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40 }}>Cargando…</div>
        ) : actas.length === 0 ? (
          <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40, fontSize: 13 }}>
            Sin entradas para esta instalación. Añade la primera.
          </div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {actas.map(a => (
              <ActaCard key={a.id} acta={a} user={user} isAdmin={isAdmin} onDelete={deleteActa} />
            ))}
          </div>
        )}

        {modal && (
          <ActaModal
            inst={selectedInst}
            tipo={tipo} setTipo={setTipo}
            titulo={titulo} setTitulo={setTitulo}
            trabajo={trabajo} setTrabajo={setTrabajo}
            materiales={materiales} setMateriales={setMateriales}
            observaciones={observaciones} setObservaciones={setObservaciones}
            estadoInst={estadoInst} setEstadoInst={setEstadoInst}
            fecha={fecha} setFecha={setFecha}
            saving={saving} onSubmit={submitActa} onClose={() => setModal(false)}
          />
        )}
      </div>
    );
  }

  // ── Vista lista ────────────────────────────────────────
  return (
    <div style={{ padding: 'clamp(12px,3vw,24px)', maxWidth: 860, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ fontSize: 20, fontWeight: 800, color: 'var(--text)' }}>Actas</div>
        {pagination.total > 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-dim)' }}>
            {pagination.total} instalaciones
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        <input
          className="sg-input"
          value={search}
          onChange={handleSearch}
          placeholder="Buscar por nombre, dirección, cliente o nº instalación…"
          style={{ flex: 1 }}
        />
        {search && (
          <button className="sg-btn-sec"
            onClick={() => { setSearch(''); setApplied(''); setPage(1); }}
            style={{ padding: '0 14px', flexShrink: 0 }}>✕</button>
        )}
      </div>

      {loadingList ? (
        <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40 }}>Cargando…</div>
      ) : instList.length === 0 ? (
        <div style={{ color: 'var(--text-dim)', textAlign: 'center', padding: 40, fontSize: 13 }}>
          Sin resultados{applied ? ` para "${applied}"` : ''}
        </div>
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {instList.map(inst => (
            <InstCard key={inst.id} inst={inst} onClick={() => setSelectedInst(inst)} />
          ))}
        </div>
      )}

      {pagination.pages > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center',
            gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
          <button className="sg-btn-sec" style={{ padding: '5px 14px' }}
            disabled={page <= 1} onClick={() => setPage(p => p - 1)}>← Anterior</button>
          <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{page} / {pagination.pages}</span>
          <button className="sg-btn-sec" style={{ padding: '5px 14px' }}
            disabled={page >= pagination.pages} onClick={() => setPage(p => p + 1)}>Siguiente →</button>
        </div>
      )}
    </div>
  );
}

function InstCard({ inst, onClick }) {
  const [showContacto, setShowContacto] = useState(false);
  const estadoColor = ESTADO_COLOR[inst.acta_estado] || null;
  const estadoLabel = ESTADO_OPTS.find(o => o.val === inst.acta_estado)?.label;

  return (
    <div
      onClick={onClick}
      style={{ background: 'var(--surface)', border: '1px solid var(--border)', minWidth: 0,
        borderRadius: 10, padding: '12px 16px', cursor: 'pointer', transition: 'border-color .15s' }}
      onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--amber)'}
      onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            onClick={e => { e.stopPropagation(); setShowContacto(s => !s); }}
            title="Ver teléfono de contacto"
            style={{ fontSize: 13, fontWeight: 800, color: 'var(--text)', cursor: 'pointer',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              marginBottom: (inst.acta_id || showContacto) ? 8 : 0 }}>
            📍 {instNombre(inst)}
          </div>

          {showContacto && <ContactoBox direccion={inst.direccion} />}

          {inst.acta_id ? (
            <div style={{ background: 'var(--surface2)', borderRadius: 7, padding: '8px 10px',
                borderLeft: `3px solid ${estadoColor || 'var(--border)'}` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dim)',
                    textTransform: 'uppercase', letterSpacing: '.05em' }}>
                  Última entrada
                </span>
                {estadoColor && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
                    background: estadoColor + '22', color: estadoColor, border: `1px solid ${estadoColor}44` }}>
                    {estadoLabel}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text)', fontWeight: 600, marginBottom: 2 }}>
                {inst.acta_titulo || (inst.acta_tipo === 'mantenimiento' ? '🔧 Mantenimiento' : '📝 Actualización')}
              </div>
              {inst.acta_trabajo && (
                <div style={{ fontSize: 11, color: 'var(--text-dim)', overflow: 'hidden',
                  textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {inst.acta_trabajo}
                </div>
              )}
              <div style={{ fontSize: 10, color: 'var(--text-dim)', marginTop: 4 }}>
                {inst.acta_fecha
                  ? new Date((inst.acta_fecha || '').split('T')[0] + 'T12:00:00').toLocaleDateString('es-ES',
                      { day: '2-digit', month: 'short', year: 'numeric' })
                  : ''}
                {inst.acta_user ? ` · ${inst.acta_user}` : ''}
              </div>
            </div>
          ) : (
            <div style={{ fontSize: 11, color: '#3a4260', fontStyle: 'italic' }}>
              Sin entradas registradas
            </div>
          )}
        </div>
        <span style={{ color: 'var(--text-dim)', fontSize: 16, marginTop: 2, flexShrink: 0 }}>›</span>
      </div>
    </div>
  );
}

const TIPO_CONTACTO_INFO = {
  responsable:   { icon: '👤', label: 'Responsable' },
  mantenimiento: { icon: '🔧', label: 'Mantenimiento' },
  seguridad:     { icon: '🛡️', label: 'Seguridad' },
  otro:          { icon: '📌', label: 'Otro' },
};

function ContactoBox({ direccion }) {
  const user    = useAuthStore(s => s.user);
  const isAdmin = ROLES_ADMIN.includes(user?.rol);
  const [contactos, setContactos] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [tipo, setTipo]           = useState('responsable');
  const [nombre, setNombre]       = useState('');
  const [telefono, setTelefono]   = useState('');
  const [saving, setSaving]       = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/api/actas/contactos?direccion=${encodeURIComponent(direccion)}`)
      .then(r => setContactos(r.data || []))
      .catch(() => setContactos([]))
      .finally(() => setLoading(false));
  }, [direccion]);

  useEffect(() => { load(); }, [load]);

  async function addContacto() {
    if (!nombre.trim() || !telefono.trim()) return;
    setSaving(true);
    try {
      await api.post('/api/actas/contactos', { direccion, tipo, nombre: nombre.trim(), telefono: telefono.trim() });
      setNombre(''); setTelefono(''); setShowForm(false);
      load();
    } catch (e) { alert('Error: ' + e.message); }
    finally { setSaving(false); }
  }

  async function aprobar(id) {
    try { await api.put(`/api/actas/contactos/${id}/aprobar`); load(); }
    catch (e) { alert('Error: ' + e.message); }
  }

  async function eliminar(id) {
    if (!window.confirm('¿Eliminar este contacto?')) return;
    try { await api.delete(`/api/actas/contactos/${id}`); load(); }
    catch (e) { alert('Error: ' + e.message); }
  }

  return (
    <div onClick={e => e.stopPropagation()}
      style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 7,
        padding: '8px 10px', marginBottom: 8 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--text-dim)', textTransform: 'uppercase',
        letterSpacing: '.05em', marginBottom: 6 }}>
        📞 Contactos
      </div>

      {loading ? (
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 6 }}>Cargando…</div>
      ) : contactos.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--text-dim)', marginBottom: 6 }}>Sin contactos registrados.</div>
      ) : (
        <div style={{ display: 'grid', gap: 5, marginBottom: 6 }}>
          {contactos.map(c => {
            const info = TIPO_CONTACTO_INFO[c.tipo] || TIPO_CONTACTO_INFO.otro;
            const pendiente = c.estado === 'pendiente';
            const canManage = isAdmin || c.created_by === user?.id;
            return (
              <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--surface)',
                border: '1px solid var(--border)', borderRadius: 6, padding: '5px 8px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: 12 }}>{info.icon}</span>
                <span style={{ fontSize: 10, color: 'var(--text-dim)', textTransform: 'uppercase', fontWeight: 700 }}>{info.label}</span>
                <span style={{ fontSize: 12, color: 'var(--text)', fontWeight: 600 }}>{c.nombre}</span>
                <span style={{ fontSize: 12, color: 'var(--text-dim)' }}>{c.telefono}</span>
                {pendiente && (
                  <span style={{ fontSize: 9, fontWeight: 800, padding: '1px 6px', borderRadius: 4,
                    background: 'rgba(251,191,36,.12)', color: '#fbbf24', border: '1px solid rgba(251,191,36,.4)' }}>
                    Pendiente de revisión
                  </span>
                )}
                <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                  {pendiente && isAdmin && (
                    <button onClick={() => aprobar(c.id)} title="Aprobar"
                      style={{ background: 'rgba(74,222,128,.12)', border: '1px solid rgba(74,222,128,.4)', color: '#4ade80',
                        borderRadius: 5, padding: '2px 6px', cursor: 'pointer', fontSize: 11 }}>
                      ✓
                    </button>
                  )}
                  {canManage && (
                    <button onClick={() => eliminar(c.id)} title="Eliminar"
                      style={{ background: 'rgba(248,113,113,.1)', border: '1px solid rgba(248,113,113,.3)', color: '#f87171',
                        borderRadius: 5, padding: '2px 6px', cursor: 'pointer', fontSize: 11 }}>
                      🗑
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showForm ? (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: 8 }}>
          <div style={{ display: 'flex', gap: 5, marginBottom: 6, flexWrap: 'wrap' }}>
            {Object.entries(TIPO_CONTACTO_INFO).map(([val, info]) => (
              <button key={val} onClick={() => setTipo(val)}
                style={{ padding: '4px 8px', borderRadius: 5, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                  background: tipo === val ? 'var(--amber)' : 'var(--surface2)',
                  color: tipo === val ? '#000' : 'var(--text-dim)',
                  border: tipo === val ? 'none' : '1px solid var(--border)' }}>
                {info.icon} {info.label}
              </button>
            ))}
          </div>
          <input value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Nombre"
            style={{ width: '100%', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6,
              color: 'var(--text)', padding: '6px 8px', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 5 }} />
          <input value={telefono} onChange={e => setTelefono(e.target.value)} placeholder="Teléfono" type="tel"
            style={{ width: '100%', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 6,
              color: 'var(--text)', padding: '6px 8px', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box', marginBottom: 6 }} />
          {!isAdmin && (
            <div style={{ fontSize: 10, color: 'var(--text-dim)', marginBottom: 6 }}>
              Quedará pendiente de revisión por un administrador.
            </div>
          )}
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={addContacto} disabled={saving || !nombre.trim() || !telefono.trim()}
              style={{ background: 'var(--amber)', color: '#000', border: 'none', padding: '5px 12px',
                borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 11,
                opacity: (saving || !nombre.trim() || !telefono.trim()) ? .6 : 1 }}>
              {saving ? 'Guardando…' : 'Guardar'}
            </button>
            <button onClick={() => { setShowForm(false); setNombre(''); setTelefono(''); }}
              style={{ background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer', fontSize: 11 }}>
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowForm(true)}
          style={{ background: 'none', border: '1px dashed var(--border)', color: 'var(--text-dim)',
            cursor: 'pointer', fontSize: 11, padding: '5px 10px', borderRadius: 6, width: '100%' }}>
          + Añadir contacto
        </button>
      )}
    </div>
  );
}

function ActaCard({ acta, user, isAdmin, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const estadoColor = ESTADO_COLOR[acta.estado_inst] || '#6b7280';
  const estadoLabel = ESTADO_OPTS.find(o => o.val === acta.estado_inst)?.label || acta.estado_inst;
  const canDelete   = isAdmin || acta.user_id === user?.id;
  const hasDetail   = acta.trabajo || acta.materiales || acta.observaciones;

  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', minWidth: 0,
        borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '12px 14px', display: 'flex', alignItems: 'flex-start', gap: 10,
          cursor: hasDetail ? 'pointer' : 'default' }}
        onClick={() => hasDetail && setExpanded(e => !e)}>
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: estadoColor,
          marginTop: 5, flexShrink: 0 }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 3 }}>
            <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--text)' }}>
              {acta.titulo || (acta.tipo === 'mantenimiento' ? '🔧 Mantenimiento' : '📝 Actualización')}
            </span>
            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4,
              background: estadoColor + '22', color: estadoColor,
              border: `1px solid ${estadoColor}44`, whiteSpace: 'nowrap' }}>
              {estadoLabel}
            </span>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-dim)' }}>
            {new Date((acta.fecha || '').split('T')[0] + 'T12:00:00').toLocaleDateString('es-ES',
              { day: '2-digit', month: 'short', year: 'numeric' })}
            {' · '}{acta.user_nombre}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          {canDelete && (
            <button onClick={e => { e.stopPropagation(); onDelete(acta.id); }}
              style={{ background: '#1a0a0a', border: '1px solid #7f1d1d', color: '#f87171',
                borderRadius: 6, padding: '4px 8px', cursor: 'pointer', fontSize: 13, lineHeight: 1 }}>
              🗑
            </button>
          )}
          {hasDetail && (
            <span style={{ color: 'var(--text-dim)', fontSize: 11 }}>{expanded ? '▲' : '▼'}</span>
          )}
        </div>
      </div>
      {expanded && hasDetail && (
        <div style={{ padding: '8px 14px 14px 32px', borderTop: '1px solid var(--border)' }}>
          {acta.trabajo       && <TextBlock label="Trabajo realizado" text={acta.trabajo} />}
          {acta.materiales    && <TextBlock label="Materiales"        text={acta.materiales} />}
          {acta.observaciones && <TextBlock label="Observaciones"     text={acta.observaciones} />}
        </div>
      )}
    </div>
  );
}

function ActaModal({ inst, tipo, setTipo, titulo, setTitulo, trabajo, setTrabajo,
    materiales, setMateriales, observaciones, setObservaciones,
    estadoInst, setEstadoInst, fecha, setFecha, saving, onSubmit, onClose }) {
  return (
    <div style={{ position: 'fixed', inset: 0, background: '#000a', display: 'flex',
        alignItems: 'flex-start', justifyContent: 'center', zIndex: 500, overflowY: 'auto' }}
      onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12,
          padding: 22, width: '90%', maxWidth: 500, margin: '40px auto' }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: 'var(--amber)', marginBottom: 16 }}>
          Nueva entrada · {instNombre(inst)}
        </div>

        <Fld label="Tipo">
          <div style={{ display: 'flex', gap: 8 }}>
            {[['actualizacion','📝 Actualización'],['mantenimiento','🔧 Mantenimiento']].map(([v,l]) => (
              <button key={v} onClick={() => setTipo(v)}
                style={{ flex: 1, background: tipo===v ? '#1a3560' : 'var(--surface2)',
                  border: `1.5px solid ${tipo===v ? '#3b82f6' : 'var(--border)'}`,
                  color: tipo===v ? '#60a5fa' : 'var(--text-dim)',
                  padding: '7px 0', borderRadius: 6, cursor: 'pointer', fontWeight: 700, fontSize: 12 }}>
                {l}
              </button>
            ))}
          </div>
        </Fld>

        <Fld label="Título (opcional)">
          <input type="text" value={titulo} onChange={e => setTitulo(e.target.value)}
            placeholder="Resumen breve" style={INP} />
        </Fld>

        <Fld label="Trabajo realizado">
          <textarea value={trabajo} onChange={e => setTrabajo(e.target.value)}
            placeholder="Describe el trabajo realizado…" rows={3}
            style={{ ...INP, resize: 'vertical' }} />
        </Fld>

        {tipo === 'mantenimiento' && (<>
          <Fld label="Materiales utilizados">
            <textarea value={materiales} onChange={e => setMateriales(e.target.value)}
              placeholder="Lista de materiales usados…" rows={2}
              style={{ ...INP, resize: 'vertical' }} />
          </Fld>
          <Fld label="Observaciones">
            <textarea value={observaciones} onChange={e => setObservaciones(e.target.value)}
              placeholder="Notas adicionales…" rows={2}
              style={{ ...INP, resize: 'vertical' }} />
          </Fld>
        </>)}

        <Fld label="Estado de la instalación">
          <select value={estadoInst} onChange={e => setEstadoInst(e.target.value)} style={INP}>
            {ESTADO_OPTS.map(o => <option key={o.val} value={o.val}>{o.label}</option>)}
          </select>
        </Fld>

        <Fld label="Fecha">
          <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} style={INP} />
        </Fld>

        <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
          <button onClick={onSubmit} disabled={saving}
            style={{ flex: 1, background: 'var(--amber)', color: '#000', border: 'none',
              padding: '9px 0', borderRadius: 7, cursor: 'pointer', fontWeight: 800,
              fontSize: 13, opacity: saving ? 0.6 : 1 }}>
            {saving ? 'Guardando…' : 'Guardar'}
          </button>
          <button onClick={onClose}
            style={{ background: 'var(--surface2)', color: 'var(--text-dim)', border: 'none',
              padding: '9px 14px', borderRadius: 7, cursor: 'pointer', fontSize: 12 }}>
            Cancelar
          </button>
        </div>
      </div>
    </div>
  );
}

function TextBlock({ label, text }) {
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ fontSize: 9, color: 'var(--amber)', fontWeight: 800,
        letterSpacing: '.08em', textTransform: 'uppercase', marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 12, color: 'var(--text)', lineHeight: 1.55, whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }}>{text}</div>
    </div>
  );
}

function Fld({ label, children }) {
  return (
    <div style={{ marginBottom: 10 }}>
      <label style={{ fontSize: 10, color: '#4e5a7a', fontWeight: 700, letterSpacing: '.05em',
        textTransform: 'uppercase', display: 'block', marginBottom: 4 }}>{label}</label>
      {children}
    </div>
  );
}

const INP = { background: 'var(--surface2)', border: '1.5px solid var(--border)', borderRadius: 6,
  color: 'var(--text)', padding: '8px 10px', width: '100%', fontSize: 13,
  fontFamily: 'inherit', boxSizing: 'border-box' };
